import { Routes } from '@angular/router';
import { MainLayoutComponent } from './layouts/main-layout/main-layout.component';
import { BlankLayoutComponent } from './layouts/blank-layout/blank-layout.component';
import { authGuard } from "./shared/auth.guard";
import { roleGuard } from "./shared/role.guard";
import { ROLES } from "./shared/roles.constants";

export const routes: Routes = [
  // ── Sin navbar/footer (auth) ──────────────────────────
  {
    path: 'login',
    component: BlankLayoutComponent,
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./pages/login/login.component').then(m => m.LoginComponent),
      },
    ],
  },
  {
    path: 'registro',
    component: BlankLayoutComponent,
    children: [
      {
        path: '', // Integrado: ya estaba en tu formato lazy-load
        loadComponent: () =>
          import('./pages/registro/registro.component').then(m => m.RegistroComponent),
      },
    ],
  },

  // ── Con navbar/footer (app principal) ─────────────────
  {
    path: '',
    component: MainLayoutComponent,
    // canActivate: [authGuard] // Descomenta esto cuando quieras proteger todo el layout
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./pages/home/home.component').then(m => m.HomeComponent),
      },
      {
        path: 'cronograma',
        loadComponent: () =>
          import('./pages/cronograma/cronograma.component').then(m => m.CronogramaComponent),
      },
      {
        path: 'agenda',
        canActivate: [authGuard, roleGuard],
        data: { roles: [ROLES.PARTICIPANTE] },
        loadComponent: () =>
          import('./pages/agenda/agenda.component').then(m => m.AgendaComponent),
      },

      // ── Rutas de Aulas ──
      {
        path: 'aulas',
        canActivate: [authGuard, roleGuard],
        data: { roles: [ROLES.ADMINISTRADOR] },
        loadComponent: () =>
          import('./pages/aulas/aula-list.component').then(m => m.AulaListComponent),
      },
      {
        path: 'aulas/crear',
        canActivate: [authGuard, roleGuard],
        data: { roles: [ROLES.ADMINISTRADOR] },
        loadComponent: () =>
          import('./pages/aulas/aula-form.component').then(m => m.AulaFormComponent),
      },
      {
        path: 'aulas/:id',
        canActivate: [authGuard, roleGuard],
        data: { roles: [ROLES.ADMINISTRADOR] },
        loadComponent: () =>
          import('./pages/aulas/aula-form.component').then(m => m.AulaFormComponent),
      },

      {
        path: 'trabajos',
        loadComponent: () =>
          import('./pages/trabajos/trabajos.component').then(m => m.TrabajosComponent),
      },
      {
        path: 'trabajos/mis-trabajos',
        canActivate: [authGuard, roleGuard],
        data: { roles: [ROLES.PARTICIPANTE, ROLES.AUTOR] },
        loadComponent: () =>
          import('./pages/trabajos/mis-trabajos.component').then(m => m.MisTrabajosComponent),
      },
      {
        path: 'trabajos/ver-trabajos',
        canActivate: [authGuard, roleGuard],
        data: { roles: [ROLES.ADMINISTRADOR, ROLES.ORGANIZADOR_CIENTIFICO] },
        loadComponent: () =>
          import('./pages/trabajos/ver-trabajos.component').then(m => m.VerTrabajosComponent),
      },
      {
        path: 'trabajos/evaluar',
        canActivate: [authGuard, roleGuard],
        data: { roles: [ROLES.EVALUADOR] },
        loadComponent: () =>
          import('./pages/trabajos/evaluar-trabajos.component').then(m => m.EvaluarTrabajosComponent),
      },

      // ── Rutas de Usuarios (Integradas desde develop) ──
      {
        path: 'usuarios',
        canActivate: [authGuard, roleGuard],
        data: { roles: [ROLES.ADMINISTRADOR, ROLES.ORGANIZADOR_CIENTIFICO] },
        loadComponent: () =>
          import('./pages/usuarios/usuario-list.component').then(m => m.UsuarioListComponent),
      },
      {
        path: 'usuario/crear',
        canActivate: [authGuard, roleGuard],
        data: { roles: [ROLES.ADMINISTRADOR] },
        loadComponent: () =>
          import('./pages/usuarios/usuario-crear.component').then(m => m.UsuarioCrearComponent),
      },
      {
        path: 'usuario/:id',
        canActivate: [authGuard, roleGuard],
        data: { roles: [ROLES.ADMINISTRADOR, ROLES.ORGANIZADOR_CIENTIFICO] },
        loadComponent: () =>
          import('./pages/usuarios/usuario-profile.component').then(m => m.UsuarioProfileComponent),
      },
      {
        path: 'mi-inscripcion',
        loadComponent: () => import('./pages/mi-inscripcion/mi-inscripcion.component').then(m => m.MiInscripcionComponent)
      },
      {
        path: 'inscripciones',
        canActivate: [authGuard, roleGuard],
        data: { roles: [ROLES.ADMINISTRADOR] },
        loadComponent: () => import('./pages/inscripciones/inscripcion-list.component').then(m => m.InscripcionListComponent)
      },
      {
        path: 'inscripcion/:id',
        canActivate: [authGuard, roleGuard],
        data: { roles: [ROLES.ADMINISTRADOR] },
        loadComponent: () => import('./pages/inscripciones/inscripcion-detail.component').then(m => m.InscripcionDetailComponent)
      },
      {
        path: 'mapa',
        loadComponent: () =>
          import('./pages/mapa/mapa.component').then(m => m.MapaComponent),
      },
      {
        path: 'cronograma/actividades/crear',
        canActivate: [authGuard, roleGuard],
        data: { roles: [ROLES.ADMINISTRADOR] },
        loadComponent: () =>
          import('./pages/actividades/actividad-form.component').then(m => m.ActividadFormComponent),
      },
      {
        path: 'cronograma/actividades/:id/detalle',
        canActivate: [authGuard],
        loadComponent: () =>
          import('./pages/actividades/actividad-detalle-page.component').then(m => m.ActividadDetallePageComponent),
      },
      {
        path: 'cronograma/actividades/:id',
        canActivate: [authGuard, roleGuard],
        data: { roles: [ROLES.ADMINISTRADOR] },
        loadComponent: () =>
          import('./pages/actividades/actividad-form.component').then(m => m.ActividadFormComponent),
      },

      // ── Rutas de Novedades ──
      {
        path: 'novedades',
        loadComponent: () =>
          import('./pages/novedades/novedades.component').then(m => m.NovedadesComponent),
      },
      {
        path: 'novedades/:id',
        loadComponent: () =>
          import('./pages/novedades/novedad-detalle.component').then(m => m.NovedadDetalleComponent),
      },
      {
        path: 'gestion/novedades',
        canActivate: [authGuard, roleGuard],
        data: { roles: [ROLES.ADMINISTRADOR] },
        loadComponent: () =>
          import('./pages/admin/novedades/novedad-list.component').then(m => m.NovedadListComponent),
      },
      {
        path: 'gestion/novedades/nueva',
        canActivate: [authGuard, roleGuard],
        data: { roles: [ROLES.ADMINISTRADOR] },
        loadComponent: () =>
          import('./pages/admin/novedades/novedad-form.component').then(m => m.NovedadFormComponent),
      },
      {
        path: 'gestion/novedades/:id/editar',
        canActivate: [authGuard, roleGuard],
        data: { roles: [ROLES.ADMINISTRADOR] },
        loadComponent: () =>
          import('./pages/admin/novedades/novedad-form.component').then(m => m.NovedadFormComponent),
      },

      // ── Configuración (admin only) ──
      {
        path: 'configuracion',
        canActivate: [authGuard, roleGuard],
        data: { roles: [ROLES.ADMINISTRADOR] },
        loadComponent: () =>
          import('./pages/configuracion/configuracion.component').then(m => m.ConfiguracionComponent),
      },

      // ── Notificaciones por email (admin only) ──
      {
        path: 'admin/notificaciones',
        canActivate: [authGuard, roleGuard],
        data: { roles: [ROLES.ADMINISTRADOR] },
        loadComponent: () =>
          import('./pages/admin/notificaciones/notificacion-shell.component').then(m => m.NotificacionShellComponent),
      },
    ],
  },

  // ── Fallback ──────────────────────────────────────────
  // Redirige cualquier ruta desconocida a la home (/).
  { path: '**', redirectTo: '' }
];
